import loadOrdersList from "@NA/Handlers/ADILoadOrdersListHandler";
import ordersListParameter from "@NA/Handlers/ADIOrdersListParameterHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getAccountsDataView } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadAccounts from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccounts";
import { getBillToState, getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import clearSearch from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/ClearSearch";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { OrderHistoryPageContext } from "@insite/content-library/Pages/OrderHistoryPage";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionHeader, AccordionSectionPanel } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Search from "@insite/mobius/Icons/Search";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";
import { priorStartDate } from "@NA/Utils/Utility";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import WarningRedIcon from "@NA/Icons/WarningRedIcon";
import Icon from "@insite/mobius/Icon";
import { FormFieldStyle } from "@insite/mobius/FormField";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";

const mapStateToProps = (state: CustomApplicationState) => {
    const billToId = state.context.session.billToId;
    const customerNumber = getBillToState(state, billToId).value?.customerNumber ?? "";
    return {
        customerNumber,
        filtersOpen: state.pages.orderHistory.filtersOpen,
        billTosDataView: getBillTosDataView(state, {}),
        accountsDataView: getAccountsDataView(state),
        ordersDataView: state.ADIGlobalData.ordersListStore.ordersList,
        getOrdersParameter: state.ADIGlobalData.ordersListStore.parameter,
        settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
    };
};

const mapDispatchToProps = {
    loadBillTos,
    loadAccounts,
    updateSearchFields,
    loadOrdersList,
    clearSearch,
    ordersListParameter,
};

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderFilterStyles {
    container?: GridContainerProps;
    filtersContainer?: GridContainerProps;
    headerGridItem?: GridItemProps;
    tabs?: InjectableCss;
    tabContainer?: InjectableCss;
    neworderStyledWrapper?: InjectableCss;
    neworderTypography?: TypographyProps;
    neworderButton?: ButtonPresentationProps;
    heading?: TypographyProps;
    headingItem?: GridItemProps;
    fromGridItem?: GridItemProps;
    toGridItem?: GridItemProps;
    datePickerStyles?: DatePickerPresentationProps;
    orderNumberText?: InjectableCss;
    buttonsItem?: GridItemProps;
    buttonsItem_Clear?: InjectableCss;
    clearSearchBtn?: InjectableCss;
    searchBtn?: ButtonProps;
    clearBtn?: ButtonProps;
    icon?: TypographyProps;
    poNumberGrid?: GridItemProps;
    gridItem?: GridItemProps;
    dropdownContainer?: InjectableCss;
    dropdownValue?: InjectableCss;
    dropdownList?: InjectableCss;
    dropdownValues?: InjectableCss;
    dropdownLabel?: InjectableCss;
    input?: TextFieldPresentationProps;
    mobilePoNumberGrid?: GridItemProps;
    Accordion?: InjectableCss;
    mtGridItem?: GridItemProps;
    mtGridItemDate?: GridItemProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    adonisErrorContainer?: InjectableCss;
    adonisErrorMsg?: InjectableCss;
    adonisErrorIcon?: InjectableCss;
}

export const ordersFilterStyles: OrderFilterStyles = {
    container: {
        css: css`
            padding-bottom: 15px;
            @media (max-width: 1079px) {
                max-width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-bottom: 0px;
                    `,
                    null,
                    null,
                ])}
            ${GridOffset} {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            width: 100%;
                            margin: 0px;
                        `,
                    null,
                    null,
                ])}
            }
        `,
    },
    filtersContainer: {
        css: css`
            ${GridItemStyle} {
                padding: 12px 15px 17px 15px;
                margin-top: 20px;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null, css` padding: 12px 10px 17px 10px; `,], "min",);
            }}
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            margin: 0px 20px 0px 0px;
                            padding: 0px;
                            margin-top: 0px;
                            justify-content: flex-end;
                            min-width: calc(25% - 20px);
                            max-width: calc(25% - 20px);
                        `,
                    null,
                    null,
                ])}
                &:first-child {
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                                max-width: calc(50% - 20px);
                                flex: 1;
                                min-width: calc(50% - 0px);
                            `,
                    null,
                    null,
                ])}
                        ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null, css` padding: 12px 10px 17px 15px; `,], "min",);
            }}
                }

                &:last-child {
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                                margin-right: 0px;
                            `,
                    null,
                    null,
                ])}
                }
                &:nth-child(3) {
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                                margin-right: 0px;
                            `,
                    null,
                    null,
                ])}
                }
            }

            ${GridOffset} {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            width: 100%;
                            margin: 0px;
                            justify-content: flex-end;
                        `,
                    null,
                    null,
                ])}
            }
            [data-test-selector="orders_fromDateFilter"] {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            max-width: calc(26% - 20px);
                            margin-right: 10px;
                            > div {
                                width: 100%;
                                > ${FormFieldStyle} {
                                    width: 100%;
                                }
                            }
                        `,
                    null,
                    null,
                ])}
                    > div { width: 100%;}
            }
            [data-test-selector="orders_toDateFilter"] {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            max-width: calc(28% - 20px);
                            > div {
                                width: 100%;
                                > ${FormFieldStyle} {
                                    width: 100%;
                                }
                            }
                        `,
                    null,
                    null,
                ])}
                    > div { width: 100%;}
            }
        `,
    },
    headingItem: {
        width: 12,
        css: css`
            padding-bottom: 5;
            border-bottom: 1px solid ${ADIColors.gray2};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        padding: 15px 0px !important;
                        margin-bottom: 11px !important;
                    `,

                    null,
                    null,
                ]);
            }}
        `,
    },
    heading: {
        variant: "h5",
        as: "h2",
        css: css`
            margin: 0;
            font-weight: bold;
            font-size: 24px;
            line-height: 1.08;
            color: ${ADIColors.darkBlue1};
        `,
    },
    headerGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            margin-top: 24px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-top: 12px !important;
                    `,
                    css`
                        margin-top: 12px !important;
                    `,
                    css`
                        padding: 0px;
                        margin-top: 0px !important;
                    `,

                    null,
                    null,
                ]);
            }}
        `,
    },
    tabs: {
        css: css`
            padding: 6.5px 10px 5.5px 10px;
            border-radius: 6px;
            border: 0;
            background-color: ${ADIColors.white};
            text-align: center;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            line-height: 22px;
            font-weight: normal;
            display: flex;
            width: 210px;
            min-height: 36px;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            transition: all 0.1s ease;
            position: relative;
            overflow: visible;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 50%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}

            &:nth-of-type(2) {
                margin: 0 0px;
            }

            &::after {
                content: "";
                width: 0px;
                height: 22px;
                margin: 6px 0 6px 1px;
                border-radius: 1px;
                background-color: ${ADIColors.gray2};
                position: absolute;
                right: -6px;
                z-index: 10;
            }

            &:last-child::after {
                display: none;
            }

            &.active {
                background-color: ${ADIColors.darkBlue1};
                color: ${ADIColors.white};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 50%;
                    `,
                    css`
                        width: 50%;
                    `,
                    css`
                        width: 50%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tabContainer: {
        css: css`
            display: flex;
            margin-top: 20px;
            padding: 2px;
            border-radius: 6px;
            border: solid 1px ${ADIColors.gray2};
            background: ${ADIColors.white};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        margin-bottom: 0 !important;
                        margin-top: 0px;
                    `,
                    css`
                        width: 100%;
                        margin-bottom: 0 !important;
                        margin-top: 0px;
                    `,
                    css`
                        width: 100%;
                        margin-top: 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    neworderStyledWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
        `,
    },
    neworderTypography: {
        css: css`
            font-size: 24px;
            font-weight: bold;
            line-height: 1.08;
            margin: 0 0 12px 0;
            padding: 0;
            color: ${ADIColors.darkBlue1};
        `,
    },
    neworderButton: {
        css: css`
            width: 100%;
            height: 40px;
            padding: 0 10px;
            margin-top: 0;
            border-radius: 4px;
            background-color: ${ADIColors.blue5};
            box-shadow: none;
            margin-bottom: 0;
            font-family: "Nunito Sans", sans-serif;
            font-size: 16px;
            font-weight: bold;
            line-height: normal;
        `,
    },
    fromGridItem: { width: [6, 6, 3, 2, 2] },
    toGridItem: { width: [6, 6, 3, 2, 2] },
    datePickerStyles: {
        calendarIconProps: {
            css: css`
                svg {
                    transform: translate(10px, 0px);
                    &:focus-within {
                        transform: translate(10px, 0px);
                    }
                }
            `,
            color: "primary",
        },
        cssOverrides: {
            datePicker: css``,
            formInputWrapper: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-top: 10px;
                        `,
                        css`
                            margin-top: 10px;
                        `,
                        css`
                            margin-top: 10px;
                        `,
                        css`
                            margin-top: 5px !important;
                        `,
                        css`
                            margin-top: 5px !important;
                        `,
                    ])}
            `,
            inputSelect: css`
                z-index: 1;
                border: 1px solid #bbb;

                &:focus {
                    border: 1px solid #bbb;
                    outline: none;
                }
                &:focus-within {
                    border: 1px solid #bbb;
                    outline: none;
                }
                > div {
                    button {
                        &:focus {
                            span {
                                outline: none;
                            }
                        }
                    }
                }
            `,
        },
    },
    clearSearchBtn: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: flex-end;
            display: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            display: flex;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    buttonsItem_Clear: {
        css: css`
            flex-direction: column;
            flex-basis: 13%;
            max-width: 13%;

            .react-datetime-picker {
                height: 40px !important;
                transform: translateY(-2px);

                :focus,
                :focus-within {
                    svg {
                        transform: translate(10px, 0px);
                        outline: 0 !important;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        margin-top: 0 !important;
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        margin-top: 0 !important;
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                    `,
                    css`
                        flex-basis: 25%;
                        max-width: 25%;
                    `,
                    css`
                        flex-basis: 25%;
                        max-width: 25%;
                        display: none;
                    `,
                    null,
                ])}
        `,
    },
    buttonsItem: {
        css: css`
            flex-direction: column;
            flex-basis: 13%;
            max-width: 13%;

            .react-datetime-picker {
                height: 40px !important;
                transform: translateY(-2px);

                :focus,
                :focus-within {
                    svg {
                        transform: translate(10px, 0px);
                        outline: 0 !important;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        margin-top: 0 !important;
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        margin-top: 0 !important;
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                    `,
                    css`
                        flex-basis: 25%;
                        max-width: 25%;
                    `,
                    css`
                        flex-basis: 25%;
                        max-width: 25%;
                    `,
                    null,
                ])}
        `,
    },
    searchBtn: {
        css: css`
            margin-top: 26px;
            width: 100%;
            border-radius: 4px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 9px;
                    `,
                    css`
                        margin-top: 9px;
                    `,
                    css`
                        margin-top: 41px;
                        padding: 0px;
                    `,
                    null,
                    null,
                ])}
            :hover {
                background-color: ${getColor("primary")};
                border-color: ${getColor("primary")};
            }
        `,
    },
    clearBtn: {
        css: css`
            margin-top: 26px;
            width: 100%;
            border-radius: 4px;
            background-color: ${ADIColors.white};
            border-color: ${getColor("primary")};
            span {
                color: #004da6;
            }

            :hover {
                background-color: ${ADIColors.white};
                border-color: ${getColor("primary")};
                span {
                    color: #004da6;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 0;
                        margin-bottom: 0;
                        padding-bottom: 0 !important;
                        border: solid 1px #004da6;
                        background-color: #fff;
                        color: #004da6 !important;
                    `,
                    css`
                        margin-top: 0;
                        margin-bottom: 0;
                        padding-bottom: 0 !important;
                        border: solid 1px #004da6;
                        background-color: #fff;
                        color: #004da6 !important;
                    `,
                    css`
                        margin-top: 41px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    icon: {
        css: css`
            transform: translateY(-1px) scale(0.9);
            svg {
                stroke: ${ADIColors.darkBlue1};
            }
        `,
    },
    poNumberGrid: {
        css: css`
            flex-basis: 20%;
            max-width: 20%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                    `,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                    `,
                    null,
                ])}
        `,
    },
    gridItem: {
        css: css`
            flex-direction: column;
            flex-basis: 13%;
            max-width: 13%;
            &[data-test-selector="ordersStatusGridItem"] {
                flex-basis: 14%;
                max-width: 14%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-basis: 25%;
                        max-width: 25%;
                    `,
                    css`
                        flex-basis: 25%;
                        max-width: 25%;
                    `,
                    null,
                ])}

            .react-datetime-picker {
                height: 40px !important;
                transform: translateY(-2px);

                :focus,
                :focus-within {
                    svg {
                        transform: translate(10px, 0px);
                        outline: 0 !important;
                    }
                }
            }
        `,
    },
    dropdownLabel: {
        css: css`
            font-weight: 700;
            text-transform: capitalize;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            margin-bottom: 6px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-bottom: 6px;
                        line-height: 22px;
                        font-weight: 800;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    dropdownContainer: {
        css: css`
            position: relative;
            width: 100%;
            .orderactive {
                z-index: 8;
            }
        `,
    },
    dropdownValue: {
        css: css`
            width: 100%;
            border: 1px solid;
            border-radius: 6px;
            padding: 9px 9px 9px 15px;
            height: 40px;
            background: ${ADIColors.white};
            border: 1px solid ${ADIColors.gray3};
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            cursor: pointer;
            position: relative;
            z-index: 10;
            display: flex;
            align-items: normal;
            justify-content: space-between;
        `,
    },
    dropdownList: {
        css: css`
            border-radius: 0 0 7px 7px;
            box-shadow: 1px 2px 14px 4px rgba(57, 57, 58, 0.25);
            background: ${ADIColors.white};
            position: absolute;
            width: 100%;
            top: 90%;
            z-index: 9;
        `,
    },
    dropdownValues: {
        css: css`
            min-height: 40px;
            border-bottom: 1px solid ${ADIColors.gray3};
            font-weight: 600;
            padding: 13px 12px;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            cursor: pointer;

            &:last-child {
                border-bottom: 0;
                border-radius: 0 0 7px 7px;
            }

            &.active,
            &:hover {
                background-color: ${ADIColors.blue1};
                color: ${ADIColors.white};
            }
        `,
    },
    input: {
        sizeVariant: "default",
        iconProps: {
            src: Search,
            css: css`
                padding: 6px;
                transform: rotate(90deg);
                height: 45px;
                width: 45px;
                svg {
                    height: 35px;
                    width: 35px;
                    color: ${getColor("common.background")};
                    top: -5px;
                    position: relative;
                }
            `,
        },
        border: "rounded",
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                border-radius: 6px;
                font-size: 14px;
                width: 100%;
                float: right;
                height: 40px;
                padding: 11px 0 11px 16px;
                font-weight: 600;
                :focus-within {
                    padding: 0px 40px 0px 10px;
                    ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                    padding: 11px 0 11px 16px;
                                    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
                                    border: 1px solid #bbb;
                                    background-color: #fff;
                                `,
                        ],
                        "max",
                    );
                }}
                }
            `,
            formInputWrapper: css`
                display: flex;
                > button {
                    width: 45px !important;
                    height: 45px !important;
                    background: ${ADIColors.blue5};
                    border-radius: 0 4px 4px 0;
                    ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                    width: 36px !important;
                                    height: 40px !important;
                                    border-radius: 0 6px 6px 0;
                                `,
                        ],
                        "max",
                    );
                }}
                    > ${IconWrapper} {
                        top: 0px;
                        right: 5px;
                        width: 35px;
                        height: 38px;
                        background: transparent !important;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            max-width: 350px;
                        `,
                    ])}
            `,
        },
    },
    mobilePoNumberGrid: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            padding-top: 0;
            padding-bottom: 0;
            margin-top: 0 !important;
            margin-bottom: 0;
        `,
    },
    Accordion: {
        css: css`
            margin-bottom: 12px;
            padding: 15px 15px 15px 15px;
            width: 100%;

            > ${AccordionSectionHeader} {
                > Button {
                    background: ${getColor("common.background")};
                    border-top-right-radius: 9px;
                    border-top-left-radius: 9px;
                    > span:last-child {
                        color: #006ae3;
                    }
                }
            }
            > ${AccordionSectionPanel} {
                background: ${getColor("common.background")};
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                                padding: 0px 0px 24px 0px;
                            `,
                    ],
                    "max",
                );
            }}
            }
        `,
    },
    mtGridItem: {
        css: css`
            flex-direction: column;
            flex-basis: 100%;
            max-width: 100%;
            margin-top: 0 !important;

            .react-datetime-picker {
                height: 40px !important;
                transform: translateY(-2px);

                :focus,
                :focus-within {
                    svg {
                        transform: translate(10px, 0px);
                        outline: 0 !important;
                    }
                }
            }
        `,
    },
    orderNumberText: {
        css: css`
            > label {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            margin-bottom: 6px;
                            line-height: 22px;
                            font-weight: 800;
                        `,
                    null,
                    null,
                ])}
            }
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            font-size: 20px;
            color: #666;
            line-height: 32px;
            font-weight: bold;
            padding: 80px 0px 80px 0;
            max-width: 738px;
            width: 100%;
            margin: 0 auto;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-top: 28px;
                        padding-bottom: 48px;
                        padding-left: 0;
                        padding-right: 0;
                        font-size: 16px;
                        font-weight: 600;
                        line-height: 1.5;
                        margin-top: 21px;
                        border-top: 1px solid #ddd;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
        css: css`
            text-align: center;
            font-size: 20px;
            color: #666;
            display: block;
            line-height: 32px;
            font-weight: bold;
            margin-top: 28px;

            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        font-size: 14px;
                        font-weight: bold;
                        line-height: 2.7;
                        margin-top: 0px;
                        &:nth-child(2) {
                            line-height: normal;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
    mtGridItemDate: {
        css: css`
            flex-direction: row;
            gap: 8px;
            flex-basis: 100%;
            max-width: 100%;
            margin-top: 0 !important;
            padding-top: 2px !important;

            .react-datetime-picker {
                height: 40px !important;
                transform: translateY(-2px);

                :focus,
                :focus-within {
                    svg {
                        transform: translate(10px, 0px);
                        outline: 0 !important;
                    }
                }
            }
        `,
    },
    adonisErrorContainer: {
        css: css`
            width: 100%;
            max-width: 100%;
            flex-basis: 100%;
            margin-top: 24px !important;
            display: flex;
            justify-content: center;
            align-items: baseline;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        align-items: flex-start;
                    `,
                    css`
                        align-items: flex-start;
                    `,
                    css`
                        align-items: flex-start;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    adonisErrorMsg: {
        css: css`
            font-size: 14px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.14;
            letter-spacing: normal;
            color: #000;
            padding: 0px;
            width: auto;
            margin-left: 5px;
        `,
    },
    adonisErrorIcon: {
        css: css`
            > svg {
                width: 17px;
                height: 14px;
            }
        `,
    },
};

const styles = ordersFilterStyles;

function useOutsideAlerter(ref: any, setaction: any) {
    React.useEffect(() => {
        function handleClickOutside(event: any) {
            if (ref.current && !ref.current.contains(event.target)) {
                setaction(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref]);
}

const tzOffset = new Date().getTimezoneOffset() * 60000;

const OrderHistoryFilter = ({
    getOrdersParameter,
    loadOrdersList,
    customerNumber,
    ordersListParameter,
    settingsCollection,
    ordersDataView,
}: Props) => {
    const dispatch = useDispatch();
    const [order, setOrder] = useState("");
    const [orderStatus, setOrderStatus] = useState("All");
    const [dateRange, setDateRange] = useState("30days");
    const [dateRangeLabel, setDateRangeLabel] = useState("Last 30 days");
    const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
    const [toDate, setToDate] = useState<Date | undefined>(new Date());
    const [customerSequence, setcustomerSequence] = useState("-1");
    const [dayscount, setDayscount] = useState(30);
    const [initLoad, setInitLoad] = useState(true);
    const [current, setCurrent] = React.useState(+(sessionStorage.getItem("orderHistoryCurrent") ?? 0));
    const [openDates, setOpenDates] = React.useState(false);
    const [openOrderStatus, setOpenOrderStatus] = React.useState(false);
    const [isMobile, setIsMobile] = React.useState(false);
    const [isCustom, setIsCustom] = React.useState(false);
    const [isSearchClicked, setSearchClicked] = React.useState(false);

    const dateRef = React.useRef(null);
    let todayDate = new Date();
    const minEndDate = new Date(new Date().setDate(todayDate.getDate() - 30));
    const maxEndDate = new Date(new Date().setDate(todayDate.getDate() + 1));
    const orderStatusRef = React.useRef(null);
    const adiOrderSettingSettings = settingsCollection.adiOrderSettingSettings;
    useOutsideAlerter(dateRef, setOpenDates);
    useOutsideAlerter(orderStatusRef, setOpenOrderStatus);
    const onLoadRef = useRef(false);

    React.useEffect(() => {
        const onWindowResize = () => {
            setIsMobile(window.innerWidth < 700);
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    React.useEffect(() => {
        const storedPreferences = localStorage.getItem("OrdersSortPreferences");
        if (storedPreferences) {
            let parsedPreferences = JSON.parse(storedPreferences);
            setDateRange(parsedPreferences.dateRange);
            ordersListParameter({
                ...getOrdersParameter,
                dateRange: parsedPreferences.dateRange,
                dateRangeDesc: parsedPreferences.dateRangeDesc,
                dateType: parsedPreferences.dateType,
                fromDate: parsedPreferences.fromDate,
                toDate: parsedPreferences.toDate,
                sort: parsedPreferences.sort,
            });
        }
    }, []);

    const orderChangeHandler = (value: string) => {
        setOrder(value);
    };

    const handleEnterForSearch = (event: any) => {
        if (event.key === "Enter") {
            searchClickHandler();
        }
    };

    const orderStatusChangeHandler = (value: string) => {
        setOrderStatus(value);
        setOpenOrderStatus(false);
        const propsStatus = { status: getStatusProperty(value) };
        ordersListParameter({
            ...getOrdersParameter,
            isSearchActive: true,
            refrencenumber: "",
            ordersearchtype: "All",
            status: value,
            fromDate: getOrdersParameter.fromDate ? getOrdersParameter.fromDate : todayDate,
            toDate: getOrdersParameter.toDate ? getOrdersParameter.toDate : "",
            sort: getOrdersParameter.sort,
            customerSequence: customerSequence,
            // currentPage: 1,
            // pageSize: 12,
            // selectedpage: 1,
            dateType: dateRange,
            dateRange: dateRange,
            dateRangeDesc: dateRangeLabel,
            poNumber: order ? order.trim() : "",
            properties: propsStatus.status === "All" ? null : JSON.stringify(propsStatus),
        });
    };

    const dateRangeChangeHandler = (value: string) => {
        setIsCustom(false);
        let localDateRangeLable = "";
        switch (value) {
            case "2weeks": {
                setDayscount(14);
                setDateRangeLabel("Last 2 weeks");
                localDateRangeLable = "Last 2 weeks";
                break;
            }
            case "30days": {
                setDayscount(30);
                setDateRangeLabel("Last 30 days");
                localDateRangeLable = "Last 30 days";
                break;
            }
            case "60days": {
                setDayscount(60);
                setDateRangeLabel("Last 60 days");
                localDateRangeLable = "Last 60 days";
                break;
            }
            case "90days": {
                setDayscount(90);
                setDateRangeLabel("Last 90 days");
                localDateRangeLable = "Last 90 days";
                break;
            }
            case "180days": {
                setDayscount(180);
                setDateRangeLabel("Last 180 days");
                localDateRangeLable = "Last 180 days";
                break;
            }
            case "custom": {
                setIsCustom(true);
                setDayscount(30);
                setDateRangeLabel("Custom");
                localDateRangeLable = "Custom";
                break;
            }
            default: {
                setDayscount(30);
                setDateRangeLabel("Last 30 days");
                localDateRangeLable = "Last 30 days";
                break;
            }
        }

        setDateRange(value);
        setOpenDates(false);
        const storedPreferences = localStorage.getItem("OrdersSortPreferences");
        let parsedPreferences;
        if (storedPreferences) {
            parsedPreferences = JSON.parse(storedPreferences);
        }
        if (parsedPreferences && Object.keys(parsedPreferences).length) {
            let selectedDates = {
                ...parsedPreferences,
                dateRange: value,
                dateRangeDesc: localDateRangeLable,
                dateType: value,
            };
            localStorage.setItem("OrdersSortPreferences", JSON.stringify(selectedDates));
        }
    };

    const convertDateToString = (date?: Date) => {
        const newDate = date ? new Date(date.getTime() - tzOffset) : "";

        if (!newDate) {
            return newDate;
        }

        const month = `0${newDate.getUTCMonth() + 1}`.slice(-2);
        const day = `0${newDate.getUTCDate()}`.slice(-2);
        const year = newDate.getUTCFullYear();
        const convertedDate = `${month}/${day}/${year}`;

        return convertedDate;
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setFromDate(selectedDay);
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setToDate(selectedDay);
    };

    const getStatusProperty = (value: string) => {
        if (value == "Credits") {
            return "CR";
        } else if (value == "Closed") {
            return "CL";
        } else if (value == "Cancelled") {
            return "CA";
        } else if (value == "Returns") {
            return "RR";
        } else if (value == "Open / Pending") {
            return "OP";
        } else if (value == "Shipped") {
            return "SH";
        } else {
            return "All";
        }
    };

    const getOrdersList = (isPriorDate: any = null, isToday: any = null) => {
        const propsStatus = { status: getStatusProperty(orderStatus) };
        const { sort } = getOrdersParameter;

        let selectedDates = {
            dateRange: dateRange,
            dateRangeDesc: dateRangeLabel,
            dateType: dateRange,
            fromDate: convertDateToString(isPriorDate),
            toDate: convertDateToString(isToday),
            sort: sort,
            DateRange: dateRange,
            DateRangeDesc: dateRange,
            Sort: sort,
            Expires: convertDateToString(isToday),
        };
        localStorage.setItem("OrdersSortPreferences", JSON.stringify(selectedDates));

        loadOrdersList({
            ...getOrdersParameter,
            filter: {
                refrencenumber: "",
                ordersearchtype: "All",
                status: orderStatus,
                fromDate: convertDateToString(isPriorDate),
                toDate: convertDateToString(isToday),
                sort,
                customerSequence: customerSequence,
                currentPage: 1,
                pageSize: 12,
                dateType: dateRange,
                selectedpage: 1,
                dateRange: dateRange,
                dateRangeDesc: dateRangeLabel,
                statusDesc: "All",
                page: 1,
                poNumber: order.trim(),
                properties: propsStatus.status !== "All" ? JSON.stringify(propsStatus) : null,
                isSearchActive: true,
            },
        });
    };

    useEffect(() => {
        if (!onLoadRef.current) {
            if (getOrdersParameter.isSearchActive) {
                setOrder(getOrdersParameter.poNumber);
                setOrderStatus(getOrdersParameter.status);
                setDateRange(getOrdersParameter.dateRange);
                setDateRangeLabel(getOrdersParameter.dateRangeDesc);
                if (getOrdersParameter.fromDate && getOrdersParameter.toDate) {
                    const parsedFromDate = new Date(getOrdersParameter.fromDate);
                    const parsedToDate = new Date(getOrdersParameter.toDate);

                    setFromDate(parsedFromDate);
                    setToDate(parsedToDate);
                }
                loadOrdersList({
                    ...getOrdersParameter,
                    filter: {
                        refrencenumber: "",
                        ordersearchtype: getOrdersParameter.ordersearchtype,
                        status: getOrdersParameter.status,
                        fromDate: getOrdersParameter.fromDate,
                        toDate: getOrdersParameter.toDate,
                        sort: getOrdersParameter.sort,
                        customerSequence: getOrdersParameter.customerSequence,
                        currentPage: getOrdersParameter.page,
                        pageSize: Number(getOrdersParameter.pageSize),
                        dateType: getOrdersParameter.dateRange,
                        selectedpage: 1,
                        dateRange: getOrdersParameter.dateRange,
                        dateRangeDesc: getOrdersParameter.dateRangeDesc,
                        statusDesc: "All",
                        page: getOrdersParameter.page,
                        poNumber: getOrdersParameter.poNumber,
                        ...(getOrdersParameter.properties
                            ? { properties: getOrdersParameter.properties }
                            : { properties: "" }),
                        isSearchActive: getOrdersParameter.isSearchActive ? getOrdersParameter.isSearchActive : true,
                    },
                });
            } else {
                const { sort } = getOrdersParameter;
                loadOrdersList({
                    ...getOrdersParameter,
                    filter: {
                        refrencenumber: "",
                        ordersearchtype: "All",
                        status: "All",
                        fromDate: "",
                        toDate: "",
                        sort,
                        customerSequence: getOrdersParameter.customerSequence,
                        currentPage: 1,
                        pageSize: 12,
                        dateType: "30days",
                        selectedpage: 1,
                        dateRange: "30days",
                        dateRangeDesc: "Last 30 days",
                        statusDesc: "All",
                        page: 1,
                        poNumber: "",
                        isSearchActive: false,
                    },
                    onComplete() {
                        onLoadRef.current = true;
                    },
                });
            }
        }
    }, []);

    useEffect(() => {
        if (isSearchClicked) {
            setSearchClicked(false);
            return;
        }

        if (!onLoadRef.current) {
            onLoadRef.current = true;
            return;
        }

        if (getOrdersParameter) {
            const storedPreferences = localStorage.getItem("OrdersSortPreferences");
            let parsedPreferences;
            if (storedPreferences) {
                parsedPreferences = JSON.parse(storedPreferences);
            }

            const filterParams = {
                refrencenumber: "",
                ordersearchtype: getOrdersParameter.ordersearchtype,
                status: getOrdersParameter.status,
                sort: getOrdersParameter.sort,
                customerSequence: getOrdersParameter.customerSequence,
                currentPage: getOrdersParameter.page,
                pageSize: Number(getOrdersParameter.pageSize),
                selectedpage: 1,
                dateRange: parsedPreferences?.dateRange || getOrdersParameter.dateRange,
                dateRangeDesc: parsedPreferences?.dateRangeDesc || getOrdersParameter.dateRangeDesc,
                dateType: parsedPreferences?.dateType || getOrdersParameter.dateType,
                fromDate: parsedPreferences?.fromDate || getOrdersParameter.fromDate,
                toDate: parsedPreferences?.toDate || getOrdersParameter.toDate,
                statusDesc: "All",
                page: getOrdersParameter.page,
                poNumber: getOrdersParameter.poNumber,
                ...(getOrdersParameter.properties ? { properties: getOrdersParameter.properties } : { properties: '' }),
                isSearchActive: getOrdersParameter.isSearchActive
            };

            if (getOrdersParameter.sort !== parsedPreferences?.sort && !getOrdersParameter._ignoreSort) {
                if (parsedPreferences) {
                    localStorage.setItem("OrdersSortPreferences", JSON.stringify({
                        ...parsedPreferences,
                        sort: getOrdersParameter.sort
                    }));
                }
            }
            loadOrdersList({
                ...getOrdersParameter,
                filter: filterParams
            });
        }
    }, [getOrdersParameter]);

    const onChangeCustomerSequence = (value: string) => {
        setcustomerSequence(value);
        onLoadRef.current = true;
        // const { sort } = getOrdersParameter;
        dispatch({
            type: "Custom/DispatchUpdateCustomerSequence",
            result: value,
        });

        setOrder(getOrdersParameter.poNumber);
        setOrderStatus(getOrdersParameter.status);
        setDateRange(getOrdersParameter.dateRange);
        setDateRangeLabel(getOrdersParameter.dateRangeDesc);
        if (getOrdersParameter.fromDate && getOrdersParameter.toDate) {
            const parsedFromDate = new Date(getOrdersParameter.fromDate);
            const parsedToDate = new Date(getOrdersParameter.toDate);

            setFromDate(parsedFromDate);
            setToDate(parsedToDate);
        }
        // loadOrdersList({
        //     ...getOrdersParameter,
        //     filter : {
        //         refrencenumber: "",
        //         ordersearchtype: getOrdersParameter.ordersearchtype,
        //         status: getOrdersParameter.status,
        //         fromDate: getOrdersParameter.fromDate,
        //         toDate: getOrdersParameter.toDate,
        //         sort: getOrdersParameter.sort,
        //         customerSequence: value,
        //         currentPage: getOrdersParameter.page,
        //         pageSize: Number(getOrdersParameter.pageSize),
        //         dateType: getOrdersParameter.dateRange,
        //         selectedpage: 1,
        //         dateRange: getOrdersParameter.dateRange,
        //         dateRangeDesc: getOrdersParameter.dateRangeDesc,
        //         statusDesc: "All",
        //         page: getOrdersParameter.page,
        //         poNumber: getOrdersParameter.poNumber,
        //         ...(getOrdersParameter.properties ? { properties: getOrdersParameter.properties } : { properties: ''}),
        //         isSearchActive: getOrdersParameter.isSearchActive
        //     }
        // });
        ordersListParameter({
            ...getOrdersParameter,
            refrencenumber: "",
            ordersearchtype: getOrdersParameter.ordersearchtype,
            status: getOrdersParameter.status,
            fromDate: getOrdersParameter.fromDate
                ? getOrdersParameter.fromDate
                : priorStartDate(adiOrderSettingSettings.orderList_MaxDateRangeInDays),
            toDate: getOrdersParameter.toDate,
            sort: getOrdersParameter.sort,
            customerSequence: value,
            currentPage: getOrdersParameter.page,
            pageSize: Number(getOrdersParameter.pageSize),
            dateType: getOrdersParameter.dateRange,
            selectedpage: 1,
            dateRange: getOrdersParameter.dateRange,
            dateRangeDesc: getOrdersParameter.dateRangeDesc,
            statusDesc: "All",
            page: getOrdersParameter.page,
            poNumber: getOrdersParameter.poNumber,
            ...(getOrdersParameter.properties ? { properties: getOrdersParameter.properties } : { properties: "" }),
            isSearchActive: getOrdersParameter.isSearchActive,
        });
    };

    const updateTab = (num: number = 0) => {
        setCurrent(num);
        sessionStorage.setItem("orderHistoryCurrent", `${num}`);
        if (num === 0) {
            onChangeCustomerSequence("-1");
        }
        if (num === 1) {
            onChangeCustomerSequence("All");
        }
    };

    const clearFiltersClickHandler = () => {
        localStorage.removeItem("OrdersSortPreferences");
        let today = new Date();
        let priorDate = new Date(new Date().setDate(today.getDate() - dayscount));

        setFromDate(priorDate);
        setToDate(today);
        setOrderStatus("All");
        setOrder("");
        setDayscount(30);
        setDateRangeLabel("Last 30 Days");
        setDateRange("30days");
        onLoadRef.current = true;

        const { sort } = getOrdersParameter;
        ordersListParameter({
            ...getOrdersParameter,
            refrencenumber: "",
            ordersearchtype: "All",
            status: "All",
            fromDate: "",
            toDate: "",
            sort,
            customerSequence: customerSequence,
            currentPage: 1,
            pageSize: 12,
            dateType: "30days",
            selectedpage: 1,
            dateRange: "30days",
            dateRangeDesc: "Last 30 days",
            statusDesc: "All",
            page: 1,
            poNumber: "",
            properties: "",
            isSearchActive: false,
        });
    };

    const searchClickHandler = () => {
        let today = new Date();
        let priorDate = new Date(new Date().setDate(today.getDate() - dayscount));
        const propsStatus = { status: getStatusProperty(orderStatus) };
        setSearchClicked(true); // new state value to stop duplicate calls
        if (isCustom) {
            ordersListParameter({
                ...getOrdersParameter,
                fromDate: convertDateToString(fromDate),
                toDate: convertDateToString(toDate),
                status: orderStatus,
                poNumber: order.trim(),
                currentPage: 1,
                page: 1,
                dateType: dateRange,
                dateRange: dateRange,
                dateRangeDesc: dateRangeLabel,
                properties: propsStatus.status !== "All" ? JSON.stringify(propsStatus) : null,
                isSearchActive: true,
            });
            getOrdersList(fromDate, toDate);
        } else {
            setFromDate(priorDate);
            setToDate(today);
            ordersListParameter({
                ...getOrdersParameter,
                fromDate: convertDateToString(priorDate),
                toDate: convertDateToString(today),
                status: orderStatus,
                poNumber: order.trim(),
                currentPage: 1,
                page: 1,
                dateType: dateRange,
                dateRange: dateRange,
                dateRangeDesc: dateRangeLabel,
                properties: propsStatus.status !== "All" ? JSON.stringify(propsStatus) : null,
                isSearchActive: true,
            });
            getOrdersList(priorDate, today);
        }
    };
    const adonisReturnCode = ordersDataView?.properties.returnCode;

    return (
        <>
            {!isMobile && (
                <Hidden below="md">
                    <GridContainer {...styles.container} data-test-selector="orders_filter">
                        <GridItem {...styles.headerGridItem}>
                            <StyledWrapper {...styles.tabContainer}>
                                <StyledWrapper
                                    {...styles.tabs}
                                    className={current === 0 ? "active" : "normal"}
                                    onClick={() => updateTab(0)}
                                >
                                    {translate("View {0}", `${customerNumber}`)}
                                </StyledWrapper>
                                <StyledWrapper
                                    {...styles.tabs}
                                    className={current === 1 ? "active" : "normal"}
                                    onClick={() => updateTab(1)}
                                >
                                    {translate("View All Locations")}
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        {adonisReturnCode != "00" && adonisReturnCode != "" && adonisReturnCode != null ? (
                            <GridItem {...styles.adonisErrorContainer}>
                                <Icon src={WarningRedIcon} {...styles.adonisErrorIcon} />
                                <Typography {...styles.adonisErrorMsg}>
                                    {translate(
                                        "Orders placed today are currently not available online. We apologize for the temporary Interruption of service and are working on resolving the issue.",
                                    )}
                                </Typography>
                            </GridItem>
                        ) : null}
                        <GridItem {...styles.headingItem}>
                            <Typography {...styles.heading}>{translate("Orders Search")}</Typography>
                        </GridItem>
                    </GridContainer>

                    <GridContainer {...styles.filtersContainer} data-test-selector="orders_filter">
                        <GridItem {...styles.poNumberGrid}>
                            <TextField
                                {...styles.orderNumberText}
                                value={order}
                                label={translate("PO Number, Order #, Reference #")}
                                onChange={event => orderChangeHandler(event.target.value)}
                                onKeyPress={event => handleEnterForSearch(event)}
                                data-test-selector="orders_orderNumberFilter"
                                placeholder="Enter number here..."
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem} data-test-selector="ordersStatusGridItem">
                            <StyledWrapper as="label" {...styles.dropdownLabel}>
                                {translate("Order Status")}
                            </StyledWrapper>
                            <StyledWrapper {...styles.dropdownContainer} ref={orderStatusRef}>
                                <StyledWrapper
                                    {...styles.dropdownValue}
                                    onClick={() => setOpenOrderStatus(!openOrderStatus)}
                                >
                                    {orderStatus}
                                    <Typography {...styles.icon}>
                                        <ChevronDown />
                                    </Typography>
                                </StyledWrapper>
                                {openOrderStatus ? (
                                    <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => orderStatusChangeHandler("All")}
                                            className={orderStatus === "All" ? "active" : "normal"}
                                        >
                                            {translate("All")}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => orderStatusChangeHandler("Cancelled")}
                                            className={orderStatus === "Cancelled" ? "active" : "normal"}
                                        >
                                            {translate("Cancelled")}
                                        </StyledWrapper>
                                        {/* <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => orderStatusChangeHandler("Closed")}
                                            className={orderStatus === "Closed" ? "active" : "normal"}
                                        >
                                            {translate("Closed")}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => orderStatusChangeHandler("Credits")}
                                            className={orderStatus === "Credits" ? "active" : "normal"}
                                        >
                                            {translate("Credits")}
                                        </StyledWrapper> */}
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => orderStatusChangeHandler("Open / Pending")}
                                            className={orderStatus === "Open / Pending" ? "active" : "normal"}
                                        >
                                            {translate("Open / Pending")}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => orderStatusChangeHandler("Returns")}
                                            className={orderStatus === "Returns" ? "active" : "normal"}
                                        >
                                            {translate("Returns")}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => orderStatusChangeHandler("Shipped")}
                                            className={orderStatus === "Shipped" ? "active" : "normal"}
                                        >
                                            {translate("Shipped")}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ) : null}
                            </StyledWrapper>
                        </GridItem>

                        <GridItem {...styles.gridItem}>
                            <StyledWrapper as="label" {...styles.dropdownLabel}>
                                {translate("Date Range")}
                            </StyledWrapper>
                            <StyledWrapper {...styles.dropdownContainer} ref={dateRef}>
                                <StyledWrapper {...styles.dropdownValue} onClick={() => setOpenDates(!openDates)}>
                                    {dateRangeLabel}
                                    <Typography {...styles.icon}>
                                        <ChevronDown />
                                    </Typography>
                                </StyledWrapper>
                                {openDates ? (
                                    <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => dateRangeChangeHandler("2weeks")}
                                            className={dateRange === "2weeks" ? "active" : "normal"}
                                        >
                                            {translate("Last 2 weeks")}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => dateRangeChangeHandler("30days")}
                                            className={dateRange === "30days" ? "active" : "normal"}
                                        >
                                            {translate("Last 30 days")}
                                        </StyledWrapper>

                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => dateRangeChangeHandler("60days")}
                                            className={dateRange === "60days" ? "active" : "normal"}
                                        >
                                            {translate("Last 60 days")}
                                        </StyledWrapper>

                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => dateRangeChangeHandler("90days")}
                                            className={dateRange === "90days" ? "active" : "normal"}
                                        >
                                            {translate("Last 90 days")}
                                        </StyledWrapper>

                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => dateRangeChangeHandler("180days")}
                                            className={dateRange === "180days" ? "active" : "normal"}
                                        >
                                            {translate("Last 180 days")}
                                        </StyledWrapper>

                                        <StyledWrapper
                                            {...styles.dropdownValues}
                                            as={"li"}
                                            onClick={() => dateRangeChangeHandler("custom")}
                                            className={dateRange === "custom" ? "active" : "normal"}
                                        >
                                            {translate("Custom")}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ) : null}
                            </StyledWrapper>
                        </GridItem>
                        {dateRange === "custom" && (
                            <>
                                <GridItem {...styles.gridItem} data-test-selector="orders_fromDateFilter">
                                    <DatePicker
                                        {...styles.datePickerStyles}
                                        label={translate("Start Date")}
                                        selectedDay={
                                            fromDate
                                                ? new Date(fromDate)
                                                : priorStartDate(adiOrderSettingSettings.orderList_MaxDateRangeInDays)
                                        }
                                        onDayChange={fromDateChangeHandler}
                                        format="MM/dd/y"
                                        dateTimePickerProps={{
                                            clearIcon: null,
                                            maxDate: toDate,
                                            minDate: priorStartDate(
                                                adiOrderSettingSettings.orderList_MaxDateRangeInDays,
                                            ),
                                        }}
                                    />
                                </GridItem>

                                <GridItem {...styles.gridItem} data-test-selector="orders_toDateFilter">
                                    <DatePicker
                                        {...styles.datePickerStyles}
                                        label={translate("End Date")}
                                        selectedDay={toDate}
                                        onDayChange={toDateChangeHandler}
                                        format="MM/dd/y"
                                        dateTimePickerProps={{
                                            clearIcon: null,
                                            maxDate: new Date(),
                                            minDate: fromDate ? new Date(fromDate) : priorStartDate(adiOrderSettingSettings.orderList_MaxDateRangeInDays),
                                        }}
                                    />
                                </GridItem>
                            </>
                        )}

                        <GridItem {...styles.buttonsItem_Clear}>
                            <Button
                                {...styles.clearBtn}
                                onClick={clearFiltersClickHandler}
                                data-test-selector="orders_clearFiltersButton"
                            >
                                {translate("Clear")}
                            </Button>
                        </GridItem>
                        <GridItem {...styles.buttonsItem_Clear}>
                            <Button
                                {...styles.searchBtn}
                                onClick={searchClickHandler}
                                data-test-selector="orders_clearFiltersButton"
                            >
                                {translate("Search")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </Hidden>
            )}

            {isMobile && (
                <Hidden above="sm">
                    <GridContainer {...styles.container} data-test-selector="orders_filter_Mobile">
                        <GridItem {...styles.headerGridItem}>
                            <StyledWrapper {...styles.tabContainer}>
                                <StyledWrapper
                                    {...styles.tabs}
                                    className={current === 0 ? "active" : "normal"}
                                    onClick={() => updateTab(0)}
                                >
                                    {translate("View {0}", `${customerNumber}`)}
                                </StyledWrapper>
                                <StyledWrapper
                                    {...styles.tabs}
                                    className={current === 1 ? "active" : "normal"}
                                    onClick={() => updateTab(1)}
                                >
                                    {translate("View All Locations")}
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        {adonisReturnCode != "00" && adonisReturnCode != "" && adonisReturnCode != null ? (
                            <GridItem {...styles.adonisErrorContainer}>
                                <Icon src={WarningRedIcon} {...styles.adonisErrorIcon} />
                                <Typography {...styles.adonisErrorMsg}>
                                    {translate(
                                        "Orders placed today are currently not available online. We apologize for the temporary Interruption of service and are working on resolving the issue.",
                                    )}
                                </Typography>
                            </GridItem>
                        ) : null}
                    </GridContainer>
                    <GridContainer {...styles.filtersContainer}>
                        <GridItem {...styles.mobilePoNumberGrid}>
                            <TextField
                                placeholder={translate("Enter number here...")}
                                type="text"
                                value={order}
                                onChange={e => setOrder(e.target.value)}
                                maxLength={25}
                                {...styles.input}
                                iconClickableProps={{ onClick: searchClickHandler }}
                            />
                        </GridItem>
                        <GridItem {...styles.buttonsItem}>
                            <Button
                                {...styles.clearBtn}
                                onClick={clearFiltersClickHandler}
                                data-test-selector="orders_clearFiltersButton"
                            >
                                {translate("Clear")}
                            </Button>
                        </GridItem>

                        <Accordion headingLevel={4} {...styles.Accordion}>
                            <AccordionSection title="Filter">
                                <GridItem {...styles.mtGridItem}>
                                    <StyledWrapper as="label" {...styles.dropdownLabel}>
                                        {translate("Order Status")}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.dropdownContainer} ref={orderStatusRef}>
                                        <StyledWrapper
                                            {...styles.dropdownValue}
                                            onClick={() => setOpenOrderStatus(!openOrderStatus)}
                                        >
                                            {orderStatus}
                                            <Typography {...styles.icon}>
                                                <ChevronDown />
                                            </Typography>
                                        </StyledWrapper>
                                        {openOrderStatus ? (
                                            <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => orderStatusChangeHandler("All")}
                                                    className={orderStatus === "All" ? "active" : "normal"}
                                                >
                                                    {translate("All")}
                                                </StyledWrapper>
                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => orderStatusChangeHandler("Cancelled")}
                                                    className={orderStatus === "Cancelled" ? "active" : "normal"}
                                                >
                                                    {translate("Cancelled")}
                                                </StyledWrapper>
                                                {/* <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => orderStatusChangeHandler("Closed")}
                                                    className={orderStatus === "Closed" ? "active" : "normal"}
                                                >
                                                    {translate("Closed")}
                                                </StyledWrapper>
                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => orderStatusChangeHandler("Credits")}
                                                    className={orderStatus === "Credits" ? "active" : "normal"}
                                                >
                                                    {translate("Credits")}
                                                </StyledWrapper> */}
                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => orderStatusChangeHandler("Open / Pending")}
                                                    className={orderStatus === "Open / Pending" ? "active" : "normal"}
                                                >
                                                    {translate("Open / Pending")}
                                                </StyledWrapper>
                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => orderStatusChangeHandler("Returns")}
                                                    className={orderStatus === "Returns" ? "active" : "normal"}
                                                >
                                                    {translate("Returns")}
                                                </StyledWrapper>
                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => orderStatusChangeHandler("Shipped")}
                                                    className={orderStatus === "Shipped" ? "active" : "normal"}
                                                >
                                                    {translate("Shipped")}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        ) : null}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.mtGridItem}>
                                    <StyledWrapper as="label" {...styles.dropdownLabel}>
                                        {translate("Date Range")}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.dropdownContainer} ref={dateRef}>
                                        <StyledWrapper
                                            {...styles.dropdownValue}
                                            className={openOrderStatus ? "orderactive" : ""}
                                            onClick={() => setOpenDates(!openDates)}
                                        >
                                            {dateRangeLabel}
                                            <Typography {...styles.icon}>
                                                <ChevronDown />
                                            </Typography>
                                        </StyledWrapper>
                                        {openDates ? (
                                            <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => dateRangeChangeHandler("2weeks")}
                                                    className={dateRange === "2weeks" ? "active" : "normal"}
                                                >
                                                    {translate("Last 2 weeks")}
                                                </StyledWrapper>
                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => dateRangeChangeHandler("30days")}
                                                    className={dateRange === "30days" ? "active" : "normal"}
                                                >
                                                    {translate("Last 30 days")}
                                                </StyledWrapper>

                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => dateRangeChangeHandler("60days")}
                                                    className={dateRange === "60days" ? "active" : "normal"}
                                                >
                                                    {translate("Last 60 days")}
                                                </StyledWrapper>

                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => dateRangeChangeHandler("90days")}
                                                    className={dateRange === "90days" ? "active" : "normal"}
                                                >
                                                    {translate("Last 90 days")}
                                                </StyledWrapper>

                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => dateRangeChangeHandler("180days")}
                                                    className={dateRange === "180days" ? "active" : "normal"}
                                                >
                                                    {translate("Last 180 days")}
                                                </StyledWrapper>

                                                <StyledWrapper
                                                    {...styles.dropdownValues}
                                                    as={"li"}
                                                    onClick={() => dateRangeChangeHandler("custom")}
                                                    className={dateRange === "custom" ? "active" : "normal"}
                                                >
                                                    {translate("Custom")}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        ) : null}
                                    </StyledWrapper>
                                </GridItem>
                                {dateRange === "custom" && (
                                    <>
                                        <GridItem {...styles.mtGridItemDate} data-test-selector="orders_fromDateFilter">
                                            <DatePicker
                                                {...styles.datePickerStyles}
                                                label={translate("Start Date")}
                                                selectedDay={
                                                    fromDate
                                                        ? new Date(fromDate)
                                                        : priorStartDate(
                                                            adiOrderSettingSettings.orderList_MaxDateRangeInDays,
                                                        )
                                                }
                                                onDayChange={fromDateChangeHandler}
                                                format="MM/dd/y"
                                                dateTimePickerProps={{
                                                    clearIcon: null,
                                                    maxDate: toDate,
                                                    minDate: priorStartDate(
                                                        adiOrderSettingSettings.orderList_MaxDateRangeInDays,
                                                    ),
                                                }}
                                            />
                                            <DatePicker
                                                {...styles.datePickerStyles}
                                                label={translate("End Date")}
                                                selectedDay={toDate}
                                                onDayChange={toDateChangeHandler}
                                                format="MM/dd/y"
                                                dateTimePickerProps={{
                                                    clearIcon: null,
                                                    minDate: fromDate,
                                                }}
                                            />
                                        </GridItem>
                                    </>
                                )}
                                <GridItem {...styles.buttonsItem}>
                                    <Button
                                        {...styles.searchBtn}
                                        onClick={searchClickHandler}
                                        data-test-selector="orders_clearFiltersButton"
                                    >
                                        {translate("Apply")}
                                    </Button>
                                </GridItem>
                            </AccordionSection>
                        </Accordion>
                    </GridContainer>
                </Hidden>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderHistoryFilter)),
    definition: {
        group: "Order History",
        displayName: "ADI Order Search Filter",
        allowedContexts: [OrderHistoryPageContext],
    },
};

export default widgetModule;
